import {
  ANALYTICS_FILTER_OPERATORS,
  ANALYTICS_LIMITS,
  type AnalyticsFilter,
  type AnalyticsFilterOperator,
  type AnalyticsQuery,
} from '@/shared/api';
import { findColumn, type AnalyticsColumn, type AnalyticsObject, type ColumnKind } from './schema';

/**
 * Filter rules mirror the backend (API/Infrastructure/AnalyticsQueryParser and
 * DAO/Analytics/AnalyticsQueryBuilder) so invalid queries are caught before a request is made.
 */

export const OPERATOR_LABELS: Record<AnalyticsFilterOperator, string> = {
  eq: 'equals',
  ne: 'does not equal',
  contains: 'contains',
  startsWith: 'starts with',
  gt: 'greater than / after',
  gte: 'at least / on or after',
  lt: 'less than / before',
  lte: 'at most / on or before',
  isNull: 'is empty (null)',
  isNotNull: 'is not empty (not null)',
};

const RANGE: readonly AnalyticsFilterOperator[] = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'];
const TEMPORAL: readonly AnalyticsFilterOperator[] = ['gte', 'lte', 'gt', 'lt', 'eq', 'ne'];
const EQUALITY: readonly AnalyticsFilterOperator[] = ['eq', 'ne'];

const VALUE_OPERATORS: Record<ColumnKind, readonly AnalyticsFilterOperator[]> = {
  text: ['contains', 'eq', 'ne', 'startsWith'],
  integer: RANGE,
  decimal: RANGE,
  float: RANGE,
  datetime: TEMPORAL,
  datetimeoffset: TEMPORAL,
  date: TEMPORAL,
  time: TEMPORAL,
  bool: EQUALITY,
  uuid: EQUALITY,
  binary: EQUALITY,
  other: EQUALITY,
};

/** Operators offered for a column; null checks only where the column is nullable. */
export function operatorsFor(column: AnalyticsColumn): readonly AnalyticsFilterOperator[] {
  const operators = VALUE_OPERATORS[column.kind];
  return column.isNullable ? [...operators, 'isNull', 'isNotNull'] : operators;
}

export function operatorNeedsValue(operator: AnalyticsFilterOperator): boolean {
  return operator !== 'isNull' && operator !== 'isNotNull';
}

function isOperator(value: string): value is AnalyticsFilterOperator {
  return (ANALYTICS_FILTER_OPERATORS as readonly string[]).includes(value);
}

// ---------- Wire format ----------

/** Serializes to the API/URL format `Column:operator[:value]` (values may contain colons). */
export function formatFilter({ column, operator, value }: AnalyticsFilter): string {
  return operatorNeedsValue(operator)
    ? `${column}:${operator}:${value ?? ''}`
    : `${column}:${operator}`;
}

/** Parses `Column:operator[:value]`, splitting on the first two colons only. */
export function parseFilter(raw: string): AnalyticsFilter | null {
  const first = raw.indexOf(':');
  if (first <= 0) return null;
  const second = raw.indexOf(':', first + 1);
  const column = raw.slice(0, first);
  const operator = second === -1 ? raw.slice(first + 1) : raw.slice(first + 1, second);
  if (!isOperator(operator)) return null;
  if (!operatorNeedsValue(operator)) return second === -1 ? { column, operator } : null;
  return second === -1 ? null : { column, operator, value: raw.slice(second + 1) };
}

/** Query-string parameters for an analytics read endpoint. */
export function toAnalyticsParams(query: AnalyticsQuery) {
  return {
    pageNumber: query.pageNumber,
    pageSize: query.pageSize,
    sortBy: query.sortBy,
    sortDirection: query.sortDirection,
    filter: query.filters.map(formatFilter),
  };
}

// ---------- Value validation ----------

const INTEGER_RANGES: Record<string, readonly [bigint, bigint]> = {
  tinyint: [0n, 255n],
  smallint: [-32_768n, 32_767n],
  int: [-2_147_483_648n, 2_147_483_647n],
  bigint: [-9_223_372_036_854_775_808n, 9_223_372_036_854_775_807n],
};

function validInteger(column: AnalyticsColumn, value: string): boolean {
  const trimmed = value.trim();
  if (!/^[+-]?\d+$/.test(trimmed)) return false;
  const [min, max] = INTEGER_RANGES[column.sqlType] ?? [
    -9_223_372_036_854_775_808n,
    9_223_372_036_854_775_807n,
  ];
  const number = BigInt(trimmed);
  return number >= min && number <= max;
}

/** Exact decimal within the column's precision and scale; never silently rounded. */
function validDecimal(column: AnalyticsColumn, value: string): boolean {
  const match = /^[+-]?(\d*)(?:\.(\d*))?$/.exec(value.trim());
  if (!match || (!match[1] && !match[2])) return false;
  const integerDigits = (match[1] ?? '').replace(/^0+/, '').length;
  const fractionDigits = (match[2] ?? '').replace(/0+$/, '').length;
  const { precision, scale } = column;
  if (precision === null || scale === null || precision === 0) return true;
  return integerDigits <= precision - scale && fractionDigits <= scale;
}

const UUID =
  /^(\{[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\}|\([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;

const DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,7})?)?)?(Z|[+-]\d{2}:\d{2})?$/i;

function hasOffset(value: string): boolean {
  return /[T ]\d{2}:\d{2}.*(Z|[+-]\d{2}:\d{2})$/i.test(value.trim());
}

function validDateTime(column: AnalyticsColumn, value: string): boolean {
  const match = DATE_TIME.exec(value.trim());
  if (!match) return false;
  const [, year, month, day, hour = '00', minute = '00', second = '00'] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day) ||
    Number(hour) > 23 ||
    Number(minute) > 59 ||
    Number(second) > 59
  ) {
    return false;
  }
  const yearNumber = Number(year);
  switch (column.sqlType) {
    case 'datetime':
      return yearNumber >= 1753;
    case 'smalldatetime':
      return yearNumber >= 1900 && yearNumber <= 2079;
    default:
      return yearNumber >= 1;
  }
}

function validBinary(column: AnalyticsColumn, value: string): boolean {
  const trimmed = value.trim();
  let bytes: number;
  if (/^0x[0-9a-f]*$/i.test(trimmed)) {
    if ((trimmed.length - 2) % 2 !== 0) return false;
    bytes = (trimmed.length - 2) / 2;
  } else if (/^[A-Za-z0-9+/]*={0,2}$/.test(trimmed) && trimmed.length % 4 === 0) {
    bytes = (trimmed.length / 4) * 3 - (/=+$/.exec(trimmed)?.[0].length ?? 0);
  } else {
    return false;
  }
  return column.maxLength === null || column.maxLength < 0 || bytes <= column.maxLength;
}

const MESSAGES: Record<ColumnKind, string> = {
  text: '',
  integer: 'Enter a whole number within the column range.',
  decimal: "Enter an exact decimal within the column's precision and scale.",
  float: 'Enter a number, e.g. 12.5 or 1e-3.',
  bool: 'Choose true or false.',
  uuid: 'Enter a UUID, e.g. 3f2504e0-4f89-11d3-9a0c-0305e82c3301.',
  datetime: 'Enter a date or date-time, e.g. 2026-10-01 or 2026-10-01T10:20.',
  datetimeoffset: 'Enter a date-time with an offset, e.g. 2026-10-01T10:20:00+02:00.',
  date: 'Enter a date as yyyy-MM-dd.',
  time: 'Enter a time between 00:00:00 and 23:59:59.',
  binary: 'Enter base64 or 0x-prefixed hexadecimal within the column length.',
  other: '',
};

function validValue(column: AnalyticsColumn, value: string): boolean {
  switch (column.kind) {
    case 'text':
    case 'other':
      return true;
    case 'integer':
      return validInteger(column, value);
    case 'decimal':
      return validDecimal(column, value);
    case 'float':
      return (
        /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(value.trim()) && Number.isFinite(Number(value))
      );
    case 'bool':
      return ['true', 'false', '1', '0'].includes(value.trim().toLowerCase());
    case 'uuid':
      return UUID.test(value.trim());
    case 'datetime':
      return validDateTime(column, value);
    case 'datetimeoffset':
      return hasOffset(value) && validDateTime(column, value);
    case 'date':
      return /^\d{4}-\d{2}-\d{2}$/.test(value) && validDateTime(column, value);
    case 'time': {
      const match = /^(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,7})?)?$/.exec(value.trim());
      return (
        match !== null &&
        Number(match[1]) <= 23 &&
        Number(match[2]) <= 59 &&
        Number(match[3] ?? '0') <= 59
      );
    }
    case 'binary':
      return validBinary(column, value);
  }
}

/** Returns an error message, or null when the filter is valid for the object. */
export function validateFilter(object: AnalyticsObject, filter: AnalyticsFilter): string | null {
  const column = findColumn(object, filter.column);
  if (!column) return `Unknown column "${filter.column}".`;
  if (!operatorsFor(column).includes(filter.operator)) {
    return `"${OPERATOR_LABELS[filter.operator]}" is not available for ${column.label}.`;
  }
  if (!operatorNeedsValue(filter.operator)) return null;

  const value = filter.value ?? '';
  if (value.length > ANALYTICS_LIMITS.maxFilterValueLength) {
    return `Values must not exceed ${ANALYTICS_LIMITS.maxFilterValueLength} characters.`;
  }
  return validValue(column, value) ? null : MESSAGES[column.kind];
}

/** Replaces the column name with the schema's canonical name (URLs may use any casing). */
export function normalizeFilter(object: AnalyticsObject, filter: AnalyticsFilter): AnalyticsFilter {
  const column = findColumn(object, filter.column);
  return column ? { ...filter, column: column.name } : filter;
}

/** Human-readable filter, e.g. `Name contains "report"`. */
export function describeFilter(object: AnalyticsObject, filter: AnalyticsFilter): string {
  const label = findColumn(object, filter.column)?.label ?? filter.column;
  const condition = OPERATOR_LABELS[filter.operator];
  return operatorNeedsValue(filter.operator)
    ? `${label} ${condition} "${filter.value ?? ''}"`
    : `${label} ${condition}`;
}

/**
 * Converts a browser `datetime-local` value to a `datetimeoffset` filter value by appending the
 * viewer's UTC offset for that moment, e.g. `2026-10-01T10:20` → `2026-10-01T10:20:00+03:00`.
 */
export function withLocalOffset(localDateTime: string): string {
  const date = new Date(localDateTime);
  if (Number.isNaN(date.getTime())) return localDateTime;
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absolute = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, '0');
  const minutes = String(absolute % 60).padStart(2, '0');
  const withSeconds = /T\d{2}:\d{2}$/.test(localDateTime) ? `${localDateTime}:00` : localDateTime;
  return `${withSeconds}${sign}${hours}:${minutes}`;
}
